import { Navigate, Outlet } from 'react-router-dom';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';

import { MobileBootScreen } from '@/components/mobile/MobileBootScreen';
import { FullScreenBrandLoader } from '@/components/shared/full-screen-brand-loader';
import { checkMFARequirementStrict } from '@/lib/auth-utils';
import { clearAllAppData } from '@/lib/mobile/auth-persistence';
import {
  clearNativeBootstrapSessionComplete,
  hasNativeBootstrapSessionComplete,
  markNativeBootstrapSessionComplete,
} from '@/lib/mobile/offline-db/bootstrap-cache';
import { isNativePlatform } from '@/lib/mobile/platform';

import { useAuth } from '../../contexts/auth-context';
import { useOrganization } from '../../contexts/organization-context';

interface ProtectedRouteProps {
  readonly redirectTo?: string;
}

interface AccessCheckInputs {
  readonly userId: string | undefined;
  readonly session: ReturnType<typeof useAuth>['session'];
  readonly loading: boolean;
  readonly isLoadingOrganizations: boolean;
  readonly hasActiveOrganizations: boolean;
  readonly isPlatformAdmin: boolean;
  readonly isBootstrapSession: boolean;
  readonly isNative: boolean;
}

interface AccessDecision {
  readonly inputs: AccessCheckInputs;
  readonly hasAccess: boolean;
  readonly requiresMFA: boolean;
}

interface AccessCheckActions {
  readonly isCurrent: () => boolean;
  readonly finish: (access: boolean, mfaRequired?: boolean) => void;
}

async function handleRequiredMFA(
  inputs: AccessCheckInputs,
  { isCurrent, finish }: AccessCheckActions,
): Promise<void> {
  if (!inputs.isNative) {
    finish(true, true);
    return;
  }
  if (!isCurrent()) {
    return;
  }

  const proofCleared = await clearNativeBootstrapSessionComplete(inputs.session);
  if (!isCurrent()) {
    return;
  }
  if (proofCleared) {
    finish(true, true);
    return;
  }

  if (!isCurrent()) {
    return;
  }
  await clearAllAppData();
  if (!isCurrent()) {
    return;
  }
  finish(false);
}

async function handleIndeterminateMFA(
  inputs: AccessCheckInputs,
  { isCurrent, finish }: AccessCheckActions,
): Promise<void> {
  if (!inputs.isNative || !inputs.session || !isCurrent()) {
    finish(false);
    return;
  }

  const hasCompletionProof = await hasNativeBootstrapSessionComplete(inputs.session);
  if (!isCurrent()) {
    return;
  }
  finish(hasCompletionProof);
}

async function handleSatisfiedMFA(
  inputs: AccessCheckInputs,
  { isCurrent, finish }: AccessCheckActions,
): Promise<void> {
  if (inputs.session && inputs.isNative) {
    if (!isCurrent()) {
      return;
    }
    await markNativeBootstrapSessionComplete(inputs.session);
    if (!isCurrent()) {
      return;
    }
  }
  finish(true);
}

async function checkRouteAccess(
  inputs: AccessCheckInputs,
  actions: AccessCheckActions,
): Promise<void> {
  if (inputs.loading) {
    return;
  }
  if (!inputs.userId) {
    actions.finish(false);
    return;
  }
  if (inputs.isLoadingOrganizations) {
    return;
  }
  if (!inputs.isPlatformAdmin && !inputs.hasActiveOrganizations) {
    actions.finish(false);
    return;
  }

  // A bootstrap session already passed the exact-session MFA proof gate.
  if (inputs.isBootstrapSession) {
    actions.finish(true);
    return;
  }

  const mfaCheck = await checkMFARequirementStrict();
  if (!actions.isCurrent()) {
    return;
  }
  if (mfaCheck.status === 'required') {
    await handleRequiredMFA(inputs, actions);
    return;
  }
  if (mfaCheck.status === 'indeterminate') {
    await handleIndeterminateMFA(inputs, actions);
    return;
  }
  await handleSatisfiedMFA(inputs, actions);
}

const ProtectedRoute = ({ redirectTo = '/auth/login' }: ProtectedRouteProps) => {
  const { user, session, loading, isBootstrapSession } = useAuth();
  const { userOrganizations, isLoadingOrganizations, isPlatformAdmin } = useOrganization();
  const [accessDecision, setAccessDecision] = useState<AccessDecision | null>(null);

  // Derive a primitive boolean so a TanStack Query refetch returning a fresh
  // userOrganizations array reference doesn't refire the access check below.
  const hasActiveOrganizations = useMemo(
    () => userOrganizations.some((om) => om.status === 'active'),
    [userOrganizations],
  );
  const userId = user?.id;
  const isNative = isNativePlatform();
  const accessCheckInputs = useMemo<AccessCheckInputs>(
    () => ({
      userId,
      session,
      loading,
      isLoadingOrganizations,
      hasActiveOrganizations,
      isPlatformAdmin,
      isBootstrapSession,
      isNative,
    }),
    [
      userId,
      session,
      loading,
      isLoadingOrganizations,
      hasActiveOrganizations,
      isPlatformAdmin,
      isBootstrapSession,
      isNative,
    ],
  );
  const isCurrentAccessCheck = useEffectEvent(
    (inputs: AccessCheckInputs) => inputs === accessCheckInputs,
  );

  useEffect(() => {
    let canceled = false;
    const inputs = accessCheckInputs;
    const isExactCurrentCheck = () => !canceled && isCurrentAccessCheck(inputs);

    const finishCheck = (access: boolean, mfaRequired = false) => {
      if (!isExactCurrentCheck()) {
        return;
      }
      setAccessDecision({ inputs, hasAccess: access, requiresMFA: mfaRequired });
    };

    const handleCheckFailure = (error: unknown) => {
      if (!isExactCurrentCheck()) {
        return;
      }
      console.error('Error checking protected route access:', error);
      finishCheck(false);
    };

    checkRouteAccess(inputs, {
      isCurrent: isExactCurrentCheck,
      finish: finishCheck,
    }).catch(handleCheckFailure);

    return () => {
      canceled = true;
    };
  }, [accessCheckInputs]);

  const hasCurrentDecision = accessDecision?.inputs === accessCheckInputs;

  if (loading || isLoadingOrganizations || !hasCurrentDecision) {
    if (isNative) {
      return <MobileBootScreen />;
    }

    return <FullScreenBrandLoader />;
  }

  // Redirect if no user or no access
  if (!user || !accessDecision.hasAccess) {
    return <Navigate replace to={redirectTo} />;
  }

  if (accessDecision.requiresMFA) {
    return <Navigate replace to="/auth/mfa-verify" />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
